import { Button } from '@data-delivery/d3s';
import { ArrowRight, CheckCircle2, FileEdit, Package, Server, XCircle } from 'lucide-react';
import type { ChangeSnapshot } from './build-change-snapshot';

interface ChangeReviewOverlayProps {
  snapshot: ChangeSnapshot;
  loading?: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

function resolveEntityName(
  id: string,
  entityMaps?: ChangeSnapshot['entityMaps'],
  type?: 'businessGroup' | 'product',
): string {
  if (!entityMaps || !type) {
    return id;
  }

  if (type === 'businessGroup' && entityMaps.businessGroups) {
    const bg = entityMaps.businessGroups.get(id);
    if (bg) {
      return bg.name || bg.shortName || id;
    }
  }

  if (type === 'product' && entityMaps.products) {
    const product = entityMaps.products.get(id);
    if (product) {
      return product.name;
    }
  }

  return id;
}

export function ChangeReviewOverlay({
  snapshot,
  loading,
  onBack,
  onConfirm,
}: ChangeReviewOverlayProps) {
  const hasChanges =
    snapshot.fields.length > 0 ||
    snapshot.productsAdded.length > 0 ||
    snapshot.productsRemoved.length > 0 ||
    snapshot.servicesAdded.length > 0 ||
    snapshot.servicesRemoved.length > 0;

  return (
    <>
      {/* Business Group Warning */}
      {snapshot.businessGroupChanged && (
        <div className='p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900 rounded-md'>
          <p className='text-sm font-medium text-amber-800 dark:text-amber-200'>
            <span role='img' aria-label='Warning'>
              ⚠️
            </span>{' '}
            Warning: Changing the Business Group will migrate all users in this assignment to the
            new Business Group.
          </p>
        </div>
      )}

      {/* Changes Content */}
      <div className='flex-1 overflow-y-auto space-y-4 min-h-[400px]'>
        {/* Field Changes */}
        {snapshot.fields.length > 0 && (
          <div className='border border-border rounded-lg p-4 bg-muted/30'>
            <h3 className='text-sm font-semibold mb-3 flex items-center gap-2'>
              <FileEdit className='h-4 w-4' />
              Field Changes
            </h3>
            <div className='space-y-3'>
              {snapshot.fields.map(f => {
                const displayBefore =
                  f.key === 'businessGroup'
                    ? resolveEntityName(f.before || '', snapshot.entityMaps, 'businessGroup')
                    : f.before || '(empty)';
                const displayAfter =
                  f.key === 'businessGroup'
                    ? resolveEntityName(f.after || '', snapshot.entityMaps, 'businessGroup')
                    : f.after || '(empty)';

                return (
                  <div
                    key={f.key}
                    className='grid grid-cols-[1fr_auto_1fr] gap-3 items-center bg-background p-3 rounded-md border border-border'
                  >
                    <div>
                      <div className='text-xs font-medium text-muted-foreground mb-1'>
                        {f.label}
                      </div>
                      <div className='text-sm text-muted-foreground line-through'>
                        {displayBefore}
                      </div>
                    </div>
                    <ArrowRight className='h-4 w-4 text-primary shrink-0' />
                    <div>
                      <div className='text-xs font-medium text-muted-foreground mb-1'>
                        {f.label}
                      </div>
                      <div className='text-sm font-medium'>{displayAfter}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Product Changes */}
        {(snapshot.productsAdded.length > 0 || snapshot.productsRemoved.length > 0) && (
          <div className='border border-border rounded-lg p-4 bg-muted/30'>
            <h3 className='text-sm font-semibold mb-3 flex items-center gap-2'>
              <Package className='h-4 w-4' />
              Product Changes
            </h3>
            <div className='space-y-3'>
              {snapshot.productsAdded.length > 0 && (
                <div className='bg-green-50 dark:bg-green-950/20 p-3 rounded-md border border-green-200 dark:border-green-900'>
                  <div className='flex items-center gap-2 mb-2'>
                    <CheckCircle2 className='h-4 w-4 text-green-600 dark:text-green-400' />
                    <span className='text-xs font-semibold text-green-700 dark:text-green-300'>
                      Added ({snapshot.productsAdded.length})
                    </span>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {snapshot.productsAdded.map(id => (
                      <span
                        key={id}
                        className='inline-flex items-center px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-xs text-green-800 dark:text-green-200'
                      >
                        {resolveEntityName(id, snapshot.entityMaps, 'product')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {snapshot.productsRemoved.length > 0 && (
                <div className='bg-red-50 dark:bg-red-950/20 p-3 rounded-md border border-red-200 dark:border-red-900'>
                  <div className='flex items-center gap-2 mb-2'>
                    <XCircle className='h-4 w-4 text-red-600 dark:text-red-400' />
                    <span className='text-xs font-semibold text-red-700 dark:text-red-300'>
                      Removed ({snapshot.productsRemoved.length})
                    </span>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {snapshot.productsRemoved.map(id => (
                      <span
                        key={id}
                        className='inline-flex items-center px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/30 text-xs text-red-800 dark:text-red-200 line-through'
                      >
                        {resolveEntityName(id, snapshot.entityMaps, 'product')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Service Changes */}
        {(snapshot.servicesAdded.length > 0 || snapshot.servicesRemoved.length > 0) && (
          <div className='border border-border rounded-lg p-4 bg-muted/30'>
            <h3 className='text-sm font-semibold mb-3 flex items-center gap-2'>
              <Server className='h-4 w-4' />
              Service Changes
            </h3>
            <div className='space-y-3'>
              {snapshot.servicesAdded.length > 0 && (
                <div className='bg-green-50 dark:bg-green-950/20 p-3 rounded-md border border-green-200 dark:border-green-900'>
                  <div className='flex items-center gap-2 mb-2'>
                    <CheckCircle2 className='h-4 w-4 text-green-600 dark:text-green-400' />
                    <span className='text-xs font-semibold text-green-700 dark:text-green-300'>
                      Added ({snapshot.servicesAdded.length})
                    </span>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {snapshot.servicesAdded.map(service => (
                      <span
                        key={service}
                        className='inline-flex items-center px-2 py-1 rounded-md bg-green-100 dark:bg-green-900/30 text-xs font-medium text-green-800 dark:text-green-200 uppercase'
                      >
                        {service.replace('sftp', 'SFTP ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {snapshot.servicesRemoved.length > 0 && (
                <div className='bg-red-50 dark:bg-red-950/20 p-3 rounded-md border border-red-200 dark:border-red-900'>
                  <div className='flex items-center gap-2 mb-2'>
                    <XCircle className='h-4 w-4 text-red-600 dark:text-red-400' />
                    <span className='text-xs font-semibold text-red-700 dark:text-red-300'>
                      Removed ({snapshot.servicesRemoved.length})
                    </span>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    {snapshot.servicesRemoved.map(service => (
                      <span
                        key={service}
                        className='inline-flex items-center px-2 py-1 rounded-md bg-red-100 dark:bg-red-900/30 text-xs font-medium text-red-800 dark:text-red-200 line-through uppercase'
                      >
                        {service.replace('sftp', 'SFTP ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className='flex gap-3 pt-4 border-t border-border'>
        <Button type='button' variant='outline' onClick={onBack} disabled={loading} size='lg'>
          Back to Edit
        </Button>
        <Button
          type='button'
          onClick={onConfirm}
          disabled={loading || !hasChanges}
          size='lg'
          className='flex-1'
        >
          {loading ? 'Submitting...' : 'Confirm & Submit Changes'}
        </Button>
      </div>
    </>
  );
}
